/**
 * @file        src/modals/FareOptionsModal.jsx
 * @description "Flight Details and Fare Options" modal opened from a flight
 *              result card's "VIEW PRICES" button.
 *
 *              ONE-WAY (single leg): the flight's real fare options, grouped into
 *              a tab per cabin the airline actually sells on it. Each card books
 *              immediately — BOOK NOW → payload.onBook(fare, price).
 *
 *              ROUND TRIP / MULTI-CITY (multiple legs): the same thing, but every
 *              leg of the trip gets its own tab (DEPART / RETURN, or Flight 1..N).
 *              A fare card here is a CHOICE, not a booking: you pick one per leg
 *              and the bar at the bottom adds them up. CONTINUE steps THROUGH the
 *              legs — on any leg but the last it moves to the next one, and only
 *              on the last does it hand every pick back at once via
 *              payload.onConfirm(picksByLegKey). That mirrors how the trip is
 *              actually priced — a round trip is only bookable once both legs are
 *              chosen, and the customer sees each leg's fares on the way there.
 *
 *              Every leg fetches its own cabins and fare rules (<LegFares/> owns
 *              that state), because TripJack returns fares only for the cabin you
 *              searched — a leg's Business/Premium/First fares exist but are
 *              withheld until you search that cabin, so we re-run the search per
 *              cabin and match THIS flight by its segment signature. The return
 *              leg lives under a different tripInfos key than the onward one, so
 *              each leg passes its own `direction` (see fetchOtherCabinFares).
 *
 *              Live fares only. A leg with no fares from the supplier shows an
 *              honest empty state — never a mock catalogue with a BOOK NOW button.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-25 Claude — Dropped the "Showing Special Return fares only." note from a leg
 *                          narrowed by a SPECIAL RETURN flight-1 fare (client) — the other
 *                          half of the 2026-09-24 removal below, so neither half of the
 *                          narrowing is announced now. Every fare on screen under a
 *                          narrowing is bookable, so the note described a filter rather
 *                          than a problem. `srNarrowed` still gates the "pick a different
 *                          one on the next tab" reassurance, which is untrue under a lock;
 *                          `isSpecialReturn` is no longer imported. Twin of the same
 *                          removal in pages/FlightResults/index.jsx.
 *   - 2026-09-25 Claude — A Student / Senior Citizen fare says so under its fare type
 *                          (the search can now run several fare types at once, so the
 *                          list mixes them). A pick cleared because another leg is on
 *                          a DIFFERENT FARE TYPE's search (same cabin) gets its own
 *                          note — the cabin wording would name a rule that was not
 *                          the one applied.
 *   - 2026-09-24 Claude — "<Brand> Exclusive Fare" is now painted in the THEME accent
 *                          (`text-neon`) on the fare-type line and on the "About …"
 *                          note, instead of a hardcoded Tailwind amber (client). Amber
 *                          is this modal's warning colour, and it is also a fixed hue
 *                          that does not deepen for the Light palette the way the
 *                          `neon` token does — so the promoted fare read as a caution
 *                          in Dark and washed out in Light.
 *   - 2026-09-24 Claude — Dropped the "Special Return fares are hidden here." note from
 *                          a leg narrowed by a REGULAR flight-1 fare (client). The fares
 *                          on screen are the bookable ones; naming the hidden family only
 *                          advertised a fare that needs flight 1 re-picked. The
 *                          Special-Return-anchor note stays — there the fares missing are
 *                          the customer's usual ones. Twin of the same removal in
 *                          pages/FlightResults/index.jsx.
 *   - 2026-09-22 Claude — The exclusive fare is the fare's NAME, not a badge beside it:
 *                          the card's fare-type line reads "<Brand> Exclusive Fare"
 *                          (fareName() in api/flights.js) and the right-hand pill added
 *                          earlier today is gone. "Best price" answers a different
 *                          question — which fare is cheapest — so two pills on one card
 *                          read as one statement split in two; the fare type is what the
 *                          customer is choosing between and it belongs in that line.
 *   - 2026-09-22 Claude — An OFFER fare wears an "<Brand> Exclusive Fare" badge in the
 *                          card's RIGHT slot, so a fare that is also the best price
 *                          keeps both pills. Its booking conditions (confirmation may
 *                          take 60 min, web check-in timing, seats subject to
 *                          availability, no GST credit) print ONCE under the fare
 *                          strip — the cards share subgrid rows, so four lines inside
 *                          one card would make every card that much taller.
 *   - 2026-09-19 Claude — (1/3/4/5) VIEW PRICES applies the same Special Return lock the
 *                          results list does: a leg after the anchor lists only fares
 *                          that pair with it (pairableFares), and says so in words —
 *                          "Showing Special Return fares only" / "Special Return fares
 *                          are hidden here" / "No fare here pairs …". This modal is the
 *                          only place the non-searched cabins surface, and those come
 *                          from their own searches, so they had never been past the
 *                          results page's filter. Picking a fare also ADVANCES to the
 *                          next flight (it only moved to a leg with no pick at all, and
 *                          every leg opens pre-seeded, so it almost never moved), and a
 *                          later leg whose fare no longer pairs is cleared with its own
 *                          `repick` wording rather than the cabin one. A leg with a
 *                          next tab carries the "you can pick a different fare for your
 *                          Return flight" line. Same for Beyond.
 *   - 2026-09-19 Claude — Wider dialog (max-w-3xl → max-w-6xl, four fare cards
 *                          across instead of three) and the cabin picker is a real
 *                          TAB STRIP: Economy / Business / … on one underlined line
 *                          with each cabin's "starting at" price INSIDE its own tab.
 *                          It was three boxed cards stacking label, price and a
 *                          marketing tagline, which spent ~56px plus the gap under
 *                          it and pushed the fare cards — the actual content — down
 *                          the screen. The taglines ("Affordable fares & value for
 *                          money" and the rest) are REMOVED for every cabin at the
 *                          client's request; the fare cards already say what really
 *                          separates the cabins. The "checking other cabins"
 *                          placeholder is an inline chip on that line rather than a
 *                          fourth box. The fare cards carry a 54vh floor so
 *                          the dialog stands slightly taller, the rules block
 *                          getting the space rather than the card footer. Same for Beyond.
 *   - 2026-09-15 Claude — The Baggage block lists PER PAX TYPE when the fare
 *                          quotes more than one (Adult / Child / Infant), the way
 *                          the supplier actually quotes it. It showed the ADULT
 *                          figure only, so a fare whose infant carries 0 Kg
 *                          checked was advertised in this comparison as carrying
 *                          the adult's 15 Kg — on the very screen the customer
 *                          uses to choose between fares. An adult-only fare is
 *                          unchanged (no redundant "Adult:" label).
 *   - 2026-09-14 Claude — ROUND TRIP Special Return gate on the final CONTINUE. The
 *                          fares can be changed per leg in here after the results bar
 *                          passed, so confirm() re-checks them with specialReturnIssue()
 *                          and opens SpecialReturnModal instead of going to review when
 *                          they can't be sold together; its CTA returns to the RETURN
 *                          tab. Multi-city and one-way are untouched.
 *   - 2026-09-12 Claude — The SELECT / SELECTED button is GONE from pick mode
 *                          (round trip + multi-city). The card already carries a
 *                          radio and a ring for exactly that, so the button only
 *                          said a second time what the radio said — and it cost the
 *                          dialog the ~46px per card that was pushing CONTINUE and
 *                          the last fare rows below the fold. The whole card stays
 *                          the control (click, plus Enter/Space now that there is
 *                          no button to tab to, with role="radio"/aria-checked so
 *                          a screen reader is told the same thing the ring shows).
 *                          One-way keeps BOOK NOW — that is an action, not a
 *                          selection. The skeleton drops its button bar in pick
 *                          mode too, so the swap still does not reflow.
 *   - 2026-09-12 Claude — SECOND compaction pass — BOOK NOW was still below the
 *                          fold. The fare card's own type drops to 11px with its
 *                          padding at px-2.5 py-2, section gaps at space-y-2 and
 *                          tick rows at space-y-0.5; section headings are 11px;
 *                          the price is text-base; the button is py-1.5. The cabin
 *                          tab, the header (16px title, smaller close button), the
 *                          itinerary row (`compact`) and every gap between them
 *                          shrink with it. Nothing was removed — every line the
 *                          card showed it still shows.
 *   - 2026-09-12 Claude — The dialog is COMPACT enough to read without scrolling it
 *                          up and down, and the fare strip now says when there is
 *                          more to its right. (a) Every cabin tab is smaller
 *                          (narrower, tighter padding, 13/11/10px type) and so is
 *                          the "Checking other cabins…" chip beside them. (b) The
 *                          fare cards' own padding and the gaps between their
 *                          sections are tightened (p-4 → px-3 py-2.5, space-y-4 →
 *                          space-y-2.5, shorter buttons), with the skeleton
 *                          following the same shape so the swap still does not
 *                          reflow. (c) Header, itinerary and the two rows sit
 *                          closer together. (d) The fare row is an <HScroller>:
 *                          same row, same card widths, plus left/right arrows that
 *                          appear only while that direction has more fares — the
 *                          scrollbar alone never told the customer a cheaper fare
 *                          was one card over. Layout, data, prices and every
 *                          handler are unchanged.
 *   - 2026-09-11 Claude — Round trip / multi-city picks are kept on ONE search. Every
 *                          cabin tab beyond the searched one is its own re-run search,
 *                          and TripJack refuses a trip whose priceIds span two ("Number
 *                          of PriceId … doesn't match with the requested trips" — the
 *                          dead-end popup on the seat step). A pick from a different
 *                          search than the other legs' now clears those legs, walks the
 *                          customer to them, opens them on the matching cabin, and says
 *                          why (`repick`). Fares carry `searchTag` (api/flights.js).
 *   - 2026-09-11 Claude — (6) Fee slabs stacked per client request: the time
 *                          range as a muted heading, the fee beneath it as a ✓ row
 *                          ("✓ ₹3,500.00 + ₹115.00"), matching the Baggage rows.
 *   - 2026-09-11 Claude — (5) Fee slabs drop the bordered block: plain rows,
 *                          range left / fee right, per client request.
 *   - 2026-09-11 Claude — (4) Fee slabs laid out as TripJack does: one row per
 *                          slab, time range left / fee right (wraps to a
 *                          right-aligned fee line on the narrow card), in a
 *                          bordered block. Display only — same data, same flows.
 *   - 2026-09-11 Claude — (3) "Flexibility" → "Cancellation Fee" + "Date Change
 *                          Fee", each listing EVERY slab of the fare rule
 *                          ("5 hrs to 74 hrs → ₹3,500.00 + ₹115.00", fee bold),
 *                          per sector when the rule covers several. Seats, Meals &
 *                          More always shows "Seat Charge — …" (SEAT_CHARGEABLE
 *                          policy text) and "Meal — …" (fd.ADULT.mI), or "NA" when
 *                          the API is silent — "Free Seats" is no longer claimed
 *                          for an absent category. Selection / booking untouched.
 *   - 2026-09-11 Claude — (2) Comment only: the ₹0.00 branch now also covers
 *                          TripJack's zero-fee shape (see fareRuleSummary.js).
 *   - 2026-09-11 Claude — Fare card Flexibility lines print the fee as its real
 *                          parts ("₹3,000.00 + ₹115.00"), and a fee the airline SENT
 *                          as ₹0 reads "✓ Cancellation: ₹0.00" / "✓ Reissue: ₹0.00"
 *                          instead of dropping to the generic fare-rules line. A
 *                          window with NO amount still falls back to that line —
 *                          TripJack omits the amount on fares that do carry a
 *                          penalty, so absent is not treated as free. Baggage KG
 *                          values are bold.
 *   - 2026-09-10 Claude — CONTINUE now WALKS THE LEGS instead of finishing from
 *                          leg 1. Both legs open pre-seeded with the fare their
 *                          results card carried, so a round trip showed ✓ on
 *                          DEPART and RETURN the moment it opened and CONTINUE
 *                          confirmed straight out of the DEPART tab — the return
 *                          leg's own fares were never seen. The button now
 *                          advances to the next leg (with a "Next: …" caption) and
 *                          only confirms on the LAST leg; advancing needs the leg
 *                          on screen chosen, confirming still needs them all. The
 *                          body also scrolls back to the top on a leg change (all
 *                          legs are mounted at once, so it kept the previous leg's
 *                          offset). Selection stays marked by the fare card's OWN
 *                          radio and nothing else — per client request, no badge,
 *                          no "Selected" line, no fare caption on the leg tabs.
 *                          onConfirm / onBook, the fare + cabin fetches and the
 *                          booking payloads are untouched.
 *   - 2026-08-18 Claude — Icon refresh: the shared glyphs (close, chevrons, check,
 *                          map pin, arrows, search, calendar) redrawn as one
 *                          consistent filled 24×24 family. Path data only — every
 *                          <svg> wrapper, class and size is unchanged.
 *   - 2026-07-27 Claude — The modal itself is the loading state now. The old
 *                          centred "Getting Fares…" spinner (an artificial 700 ms
 *                          delay that also held BACK the supplier calls) is gone:
 *                          the real dialog opens at once with skeleton fare cards
 *                          — one per fare we already know about, so there is no
 *                          reflow — and each cabin's cards swap to real content
 *                          only when EVERY fare rule for that cabin has landed.
 *                          Cards no longer pop in half-empty and fill line by line.
 *   - 2026-07-12 Claude — Round trip + multi-city now get the same fare-options
 *                          experience one-way had: a tab per leg, each with its
 *                          own cabin tabs and real fare cards, plus a combined
 *                          total + CONTINUE bar. Per-leg state moved into
 *                          <LegFares/>; the cross-cabin lookup is now direction-
 *                          aware (a return leg is not under ONWARD). One-way
 *                          behaviour is unchanged — it is simply the 1-leg case.
 *   - 2026-07-12 Claude — Header flight line replaced by the shared, collapsible
 *                          <FlightItinerary>.
 *   - 2026-07-09 Claude — Show EVERY cabin the airline sells on this flight, not
 *                          just the searched one. Cabin tabs, per-fare rules
 *                          (cancellation / date-change / seat chargeability) and
 *                          an earned "Recommended" badge.
 *   - 2026-07-20 Claude — Fare cards in a row are equal height again, so BOOK NOW
 *                          lines up across them. The card root carried `h-full`,
 *                          which opts an item OUT of the row's align-items:stretch
 *                          (stretch applies only to an auto cross size), leaving each
 *                          card at its own content height — a fare missing a line
 *                          floated its button above its neighbours'. Dropped h-full
 *                          and pinned the footer with mt-auto.
 *   - 2026-07-09 Claude — Fare cards restructured to Baggage / Flexibility /
 *                          "Seats, Meals & More", from the real /fare-rule data.
 *   - 2026-06-25 Claude — Live per-flight fare options from the search response
 *                          (real prices + baggage + priceId).
 *   - 2026-06-22 Claude — Initial port from design HTML.
 */
import { useEffect, useRef, useState } from 'react';
import { inr } from '../lib/format.js';
import { feeText, BoldKg, fmtHours } from '../lib/fareDisplay.jsx';
import { concessionFareLabel, fetchFareRule, fetchOtherCabinFares, specialReturnIssue, pairableFares, isExclusiveFare, EXCLUSIVE_FARE_LABEL } from '../api/flights.js';
import SpecialReturnModal from '../components/SpecialReturnModal.jsx';
import { summarizeFareRule } from '../lib/fareRuleSummary.js';
import FlightItinerary from '../components/FlightItinerary.jsx';

// TripJack returns FULL-WORD cabin codes on fd.ADULT.cc (ECONOMY /
// PREMIUM_ECONOMY / BUSINESS / FIRST), NOT single letters — key by those.
const CABIN_LABEL = { ECONOMY: 'Economy', PREMIUM_ECONOMY: 'Premium Economy', BUSINESS: 'Business', FIRST: 'First' };
const cabinName = (cc) => CABIN_LABEL[String(cc || '').toUpperCase()] || null;

function FareRow({ type, text }) {
  const icon = type === 'yes'
    ? <span className="text-emerald-400">✓</span>
    : type === 'warn'
      ? <span className="text-amber-400">●</span>
      : <span className="text-rose-400">✕</span>;
  const col = type === 'no' ? 'text-muted' : 'text-ink';
  return (
    <p className={`flex items-start gap-1.5 ${col} leading-snug`}>
      <span className="shrink-0">{icon}</span>
      <span>{text}</span>
    </p>
  );
}

// Cabin-class code → label (real per-fare value from the supplier).
const CABIN_LABELS = {
  ECONOMY: 'Economy cabin',
  PREMIUM_ECONOMY: 'Premium Economy cabin',
  BUSINESS: 'Business cabin',
  FIRST: 'First class cabin',
};
const cabinLabel = (cc) => CABIN_LABELS[String(cc || '').toUpperCase()] || 'Economy cabin';

// Every slab the fare rule returned for one category — the time range as a small
// heading, its fee on the line below behind the card's ✓ marker (the same shape
// as the Baggage rows) — in the API's own order (ascending `st` in every logged
// response), for every sector the rule covers (a combo / connecting fare carries
// 2–3, each labelled). Hours are TripJack's st/et, hours before departure. The
// fee is amount + additionalFee exactly as sent, in bold; a slab with no amount
// at all shows the airline's own policy text rather than a number we lack.
// `pick` names the category on each summary sector: 'cancel' | 'dateChange'.
function FeeSlabs({ sectors, pick, loading, fallback }) {
  if (loading) return null;
  if (!Array.isArray(sectors) || !sectors.length) {
    return <div className="space-y-0.5"><FareRow type={fallback[0]} text={fallback[1]} /></div>;
  }
  const multi = sectors.length > 1;
  return (
    <div className="space-y-0.5">
      {sectors.map((s) => (
        <div key={s.sector} className="space-y-0.5">
          {multi && <p className="text-[11px] font-semibold text-muted">{s.sector}</p>}
          {(s[pick] || []).length ? (
            // Per slab: "5 hrs to 74 hrs" as a muted heading, then
            // "✓ ₹3,500.00 + ₹115.00" beneath it through FareRow, so the fee
            // lines start with the same ✓ as "✓ Cabin: 7 Kg" above. Stacked, the
            // pair never has to share the narrow card's width. A slab with no
            // amount shows the airline's policy text behind the neutral ● marker.
            <div className="space-y-0.5">
              {s[pick].map((w, i) => (
                <div key={i}>
                  <p className="text-muted">{fmtHours(w.st)} to {fmtHours(w.et)}</p>
                  {w.total != null
                    ? <FareRow type="yes" text={<strong className={`font-bold ${w.total === 0 ? 'text-emerald-400' : ''}`}>{feeText(w.amount, w.addFee)}</strong>} />
                    : <FareRow type="warn" text={w.policy || 'As per fare rules'} />}
                </div>
              ))}
            </div>
          ) : <FareRow type="warn" text="NA" />}
        </div>
      ))}
    </div>
  );
}
// Booking conditions that apply to an OFFER fare and to nothing else. Shown once,
// under the fare strip, whenever such a fare is on screen — not inside each card:
// the cards are a subgrid whose rows are shared across the whole strip, so four
// extra lines in one card makes EVERY card that much taller.
const EXCLUSIVE_FARE_TERMS = [
  ['Booking Confirmation', 'May take up to 60 minutes.'],
  ['Web Check-In', "Available on the airline's website, starting one day before departure after 6 PM."],
  ['Seat Availability', 'Seats are subject to availability. If unavailable, a refund will be issued.'],
  ['GST Credit', 'Not applicable for offer fare bookings.'],
];

function ExclusiveFareNote() {
  return (
    <div className="mt-2 rounded-xl border border-neon/40 bg-neon/[0.06] px-3.5 py-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-neon">
        About {EXCLUSIVE_FARE_LABEL}s
      </p>
      <dl className="mt-1.5 space-y-1">
        {EXCLUSIVE_FARE_TERMS.map(([term, detail]) => (
          <div key={term} className="text-[11px] leading-snug">
            <dt className="inline font-semibold text-ink">{term}:</dt>{' '}
            <dd className="inline text-muted">{detail}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


// ---- LIVE fare card (real /air-search fare option) -------------------------
// `rule` is the summarised /fare-rule result for THIS fare: null while loading,
// false when the lookup failed / returned nothing, else the summary object.
//
// `pick` mode (round trip / multi-city) turns the card into a radio choice: the
// whole card selects, and the booking happens later from the CONTINUE bar once
// every leg has a fare. `book` mode (one-way) keeps the immediate BOOK NOW.
function LiveFareCard({ fare, onBook, onPick, picked = false, mode = 'book', className = '', best = false, recommended = false, rule }) {
  const loading = rule === undefined;
  const r = rule || null;

  // Cancellation / Date Change fees render every slab of the fare rule
  // (<FeeSlabs>). When no rule could be read at all, each falls back to the
  // fare's own refundable flag (search fd.ADULT.rT), in words — never an
  // invented amount.
  // The fare's allowance per PAX TYPE (api/flights.js baggageByPaxOf), already
  // ordered Adult -> Child -> Infant. Empty on an older cached search payload,
  // which is exactly when the Baggage block falls back to `fare.baggage`.
  const paxBags = Array.isArray(fare.baggageByPax) ? fare.baggageByPax : [];

  const cancelFallback = fare.refundable === true
    ? ['yes', 'Refundable — as per fare rules']
    : fare.refundable === false ? ['no', 'Non-refundable'] : ['warn', 'As per fare rules'];
  const dateChangeFallback = ['warn', 'As per fare rules'];

  // Seats, Meals & More — every service is SHOWN, with the API's own value or
  // "NA" when the API says nothing; never hidden, never guessed.
  //   Seat Charge ← fare rule SEAT_CHARGEABLE[].policyInfo ("Paid Seat", …) —
  //                 the text TripJack's own Compare view prints — or its amount
  //                 when one is sent. Absent category / blank policy → "NA".
  //                 (It used to say "Free Seats" whenever the category was
  //                 absent — a claim the supplier never made.)
  //   Meal        ← search fd.ADULT.mI: true → Complimentary, false → Chargeable.
  //                 TripJack omits mI on most fares → "NA".
  const extras = [];
  if (!loading) {
    const seat = r?.seat;
    if (seat && seat.amount > 0) {
      extras.push(['warn', <>Seat Charge — <strong className="font-bold">{feeText(seat.amount, seat.addFee)}</strong>{seat.text ? ` · ${seat.text}` : ''}</>]);
    } else {
      extras.push(['warn', `Seat Charge — ${seat?.text || 'NA'}`]);
    }
    if (fare.mealIncluded === true) extras.push(['yes', 'Meal — Complimentary']);
    else if (fare.mealIncluded === false) extras.push(['warn', 'Meal — Chargeable']);
    else extras.push(['warn', 'Meal — NA']);
  }

  const isPick = mode === 'pick';
  const highlight = isPick ? picked : (best || recommended);

  // Card layout note: the card is a SUBGRID, not a flex column, and its six
  // children (price header, Baggage, Cancellation Fee, Date Change Fee, Seats &
  // More, BOOK NOW) each take one row of the PARENT grid — so every card's
  // Baggage block starts on the same pixel as its neighbours', and so does every
  // divider between them. Comparing two fares is reading ACROSS one row, which a
  // flex column could not give: each card sized its own blocks, so a fare with a
  // four-slab cancellation rule pushed its "Seats, Meals & More" below the next
  // card's, and the customer had to hunt for the line to compare.
  //
  // Consequences to keep in mind when editing:
  //   - The six children must stay DIRECT children of this root (a wrapper div
  //     would make them one row again) and stay in step with the parent's
  //     grid-rows-[…] and the row-span-* in `className` (see <LegFares/>).
  //   - Row 5 (Seats & More) is the 1fr row, so a card shorter than the row's
  //     54vh floor grows THERE and BOOK NOW stays on the bottom edge. `flex-1` /
  //     `mt-auto` did that job before and are no longer needed.
  //   - Subgrid is Chrome 117+ / Safari 16+ / Firefox 71+. Without it the rows
  //     collapse to per-card sizing — the old behaviour, not a broken layout.
  return (
    <div
      onClick={isPick ? () => onPick?.(fare) : undefined}
      role={isPick ? 'radio' : undefined}
      aria-checked={isPick ? picked : undefined}
      tabIndex={isPick ? 0 : undefined}
      onKeyDown={isPick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick?.(fare); }
      } : undefined}
      className={`relative rounded-xl border bg-night/40 grid grid-rows-subgrid text-[11px] transition hover:-translate-y-0.5 hover:border-neon/60 hover:shadow-[0_10px_30px_-12px_rgba(52,239,164,0.35)] ${highlight ? 'border-neon/60 ring-1 ring-neon/40' : 'border-line'} ${isPick ? 'cursor-pointer' : ''} ${className}`}
    >
      {(best || recommended) && (
        <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-neon text-onaccent text-[9px] font-bold uppercase tracking-wide shadow">
          {best ? 'Best price' : 'Recommended'}
        </span>
      )}
      <div className="px-2.5 py-2 border-b border-line">
        <div className="flex items-start gap-2">
          {isPick && (
            <span
              className={`mt-1 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${picked ? 'border-neon' : 'border-line'}`}
              aria-hidden="true"
            >
              {picked && <span className="w-2 h-2 rounded-full bg-neon" />}
            </span>
          )}
          <div className="min-w-0">
            <p>
              {/* Lead with the PER-ADULT fare (matches the reference layout and the
                  fare.price that onBook passes). The all-pax total is shown beneath
                  so a multi-pax search still sees what it will actually pay. There is
                  no struck-through "was" price — the supplier gives us no such figure
                  and we will not fabricate a discount. */}
              <span className="font-display text-base font-extrabold text-neon">{inr(fare.price)}</span>{' '}
              <span className="text-[10px] text-muted">per adult</span>
            </p>
            {fare.total != null && fare.total !== fare.price && (
              <p className="text-[10px] text-muted mt-0.5">{inr(fare.total)} total</p>
            )}
            {/* The fare TYPE. An exclusive fare is named here and nowhere else — in
                the THEME accent (`neon`, whichever colour this build and the active
                Light/Dark palette make that), so it still reads as the special thing
                it is without a second pill competing with "Best price" for the same
                corner. Deliberately no longer amber: amber is this modal's WARNING
                colour (a missing baggage allowance, a cleared pick, a blocked pair),
                and a promoted fare is not a warning. The "About …" note below uses
                the same token, so the name and its explanation stay one statement. */}
            <p className={`text-[10px] tracking-wide uppercase ${isExclusiveFare(fare) ? 'font-bold text-neon' : 'text-muted'}`}>{fare.name}</p>
            {/* Which fare TYPE search this fare came from, when it is a concession —
                booking it asks each passenger for the matching ID card. */}
            {concessionFareLabel(fare.pft) && (
              <p className="text-[10px] font-bold uppercase tracking-wide text-neon">{concessionFareLabel(fare.pft)}</p>
            )}
          </div>
        </div>
      </div>
      <div className="px-2.5 py-2">
          <p className="font-semibold text-ink text-[11px] mb-0.5">Baggage</p>
          <div className="space-y-0.5">
            {/* A tick means the airline SAID so. No allowance in the fare → "NA",
                flagged amber — never a comforting default we made up.

                The allowance is quoted PER PAX TYPE, and on a fare where the
                types differ (an infant's 0 Kg checked bag against the adult's
                15 Kg is the everyday case) showing only the adult's figure here
                misprices the trip in the customer's head before they book. So a
                fare quoting more than one type lists them; a fare quoting one
                keeps the original two lines exactly, which is the vast majority
                of adult-only searches and should not gain a redundant "Adult:"
                label. */}
            {paxBags.length > 1 ? paxBags.map((p) => (
              <FareRow
                key={p.type}
                type={p.checkIn || p.cabin ? 'yes' : 'warn'}
                text={<>
                  <span className="text-muted">{p.label}</span>{' · '}
                  Cabin: {p.cabin ? <BoldKg text={p.cabin} /> : 'NA'}{' · '}
                  Check-in: {p.checkIn ? <BoldKg text={p.checkIn} /> : 'NA'}
                </>}
              />
            )) : (
              <>
                <FareRow type={fare.baggage?.cabin ? 'yes' : 'warn'} text={fare.baggage?.cabin ? <>Cabin: <BoldKg text={fare.baggage.cabin} /></> : 'Cabin baggage: NA'} />
                <FareRow type={fare.baggage?.checkIn ? 'yes' : 'warn'} text={fare.baggage?.checkIn ? <>Check-in: <BoldKg text={fare.baggage.checkIn} /></> : 'Check-in baggage: NA'} />
              </>
            )}
          </div>
      </div>
      {/* Each block from here down is its own PARENT grid row (see the layout note
          above) and carries the divider on its top edge — drawn per card, but on a
          shared row, so the rules line up straight across the whole strip. */}
      <div className="px-2.5 py-2 border-t border-line">
          <p className="font-semibold text-ink text-[11px] mb-0.5">Cancellation Fee</p>
          <FeeSlabs sectors={r?.sectors} pick="cancel" loading={loading} fallback={cancelFallback} />
      </div>
      <div className="px-2.5 py-2 border-t border-line">
          <p className="font-semibold text-ink text-[11px] mb-0.5">Date Change Fee</p>
          <FeeSlabs sectors={r?.sectors} pick="dateChange" loading={loading} fallback={dateChangeFallback} />
      </div>
      <div className="px-2.5 py-2 border-t border-line">
          <p className="font-semibold text-ink text-[11px] mb-0.5">Seats, Meals &amp; More</p>
          <div className="space-y-0.5">
            <FareRow type="yes" text={cabinLabel(fare.cabin)} />
            {extras.map((x, i) => <FareRow key={i} type={x[0]} text={x[1]} />)}
            {fare.seats > 0 && fare.seats <= 5 && (
              <FareRow type="warn" text={`Only ${fare.seats} seat${fare.seats > 1 ? 's' : ''} left`} />
            )}
          </div>
      </div>
      {/* One-way only. In pick mode the card itself is the control — the radio
          and the ring around it already say what is chosen, and a "SELECTED"
          chip repeating that under every fare is what pushed the last rows and
          the CONTINUE bar off the screen. */}
      {!isPick && (
        <div className="px-2.5 py-2 border-t border-line">
          <button
            onClick={() => onBook(fare, fare.price)}
            className="w-full py-1.5 rounded-lg text-[13px] bg-gradient-to-r from-neon to-mint text-onaccent font-bold cursor-pointer hover:brightness-110 transition"
          >BOOK NOW</button>
        </div>
      )}
    </div>
  );
}

// ---- Skeletons -------------------------------------------------------------
// Shown while a cabin's fare rules are still in flight. The fare PRICES arrive
// with the search, but cancellation / date-change / seat-chargeability come from
// a separate /fare-rule call per fare — so a card rendered immediately would
// appear half-written and then fill in line by line. One skeleton per known fare,
// at the real card width, keeps the swap to real content free of any reflow.
const SkeletonBar = ({ className = '' }) => (
  <span className={`block rounded bg-line/70 animate-pulse ${className}`} />
);

function FareCardSkeleton({ className = '', mode = 'book' }) {
  return (
    <div className={`rounded-xl border border-line bg-night/40 flex flex-col ${className}`} aria-hidden="true">
      <div className="px-2.5 py-2 border-b border-line space-y-2">
        <SkeletonBar className="h-5 w-24" />
        <SkeletonBar className="h-2.5 w-16" />
      </div>
      {/* Baggage / Flexibility / Seats, Meals & More — same three blocks the real
          card renders, so the skeleton is the card's own shape, not a grey slab. */}
      <div className="px-2.5 py-2 space-y-2 flex-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1">
            <SkeletonBar className="h-3 w-24" />
            <SkeletonBar className="h-2.5 w-full" />
            <SkeletonBar className="h-2.5 w-4/5" />
          </div>
        ))}
      </div>
      {mode !== 'pick' && (
        <div className="px-2.5 py-2 border-t border-line mt-auto">
          <SkeletonBar className="h-8 w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

/**
 * A horizontal strip that carries its OWN left / right arrows.
 *
 * The fare row scrolls sideways whenever a flight sells more fares than fit
 * across the dialog, and the only sign of that was the scrollbar beneath it — a
 * customer who never dragged it never learned there was another fare one card to
 * the right. The arrows are absolutely positioned ON the strip, so the cards keep
 * the row, the gaps and the widths they already had, and each one shows only
 * while that direction still has something to scroll to.
 *
 * @param {string} [className]    - classes for the positioned WRAPPER (margins).
 * @param {string} [rowClassName] - classes for the scrolling row itself.
 * @param {number} [step]         - px per click (one card + its gap).
 */
function HScroller({ children, className = '', rowClassName = '', step = 238 }) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  // What is scrollable changes after mount, not at it: the searched cabin's
  // fares render first, the other cabins' arrive a search later, and the cards
  // swap from skeleton to real content. So re-measure on scroll, on a resize of
  // the strip or any card, and whenever the children change.
  const sync = () => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft < max - 4 });
  };
  useEffect(() => {
    sync();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    window.addEventListener('resize', sync);
    return () => { ro.disconnect(); window.removeEventListener('resize', sync); };
  }, [children]);

  const by = (dir) => ref.current?.scrollBy({ left: dir * step, behavior: 'smooth' });

  return (
    <div className={`relative ${className}`}>
      <div ref={ref} onScroll={sync} className={`overflow-x-auto nh-scroll ${rowClassName}`}>
        {children}
      </div>
      {edges.left && (
        <button
          type="button"
          onClick={() => by(-1)}
          aria-label="Show previous fares"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-panel border border-line shadow-lg flex items-center justify-center text-ink hover:border-neon cursor-pointer"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M14 6.4 8.4 12l5.6 5.6 1.4-1.4-4.2-4.2 4.2-4.2z" /></svg>
        </button>
      )}
      {edges.right && (
        <button
          type="button"
          onClick={() => by(1)}
          aria-label="Show more fares"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-panel border border-line shadow-lg flex items-center justify-center text-ink hover:border-neon cursor-pointer"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="m10 6.4 5.6 5.6-5.6 5.6-1.4-1.4 4.2-4.2-4.2-4.2z" /></svg>
        </button>
      )}
    </div>
  );
}

// A REAL tab, not a card: one line, underlined when active, with the cabin's
// "starting at" price inside it. The boxed three-line version (label / price /
// marketing tagline) cost ~56px of vertical space and a gap above the fare cards
// for one fact and one slogan — the fare cards sit directly under the tab that
// selects them now, and the slogan is gone entirely (client request): what
// separates these cabins is on the cards themselves, in real baggage, fees and
// rules, so "Affordable fares & value for money" only took room from it.
const CabinTab = ({ label, start, active, onClick }) => (
  <button
    onClick={onClick}
    role="tab"
    aria-selected={active}
    className={`shrink-0 -mb-px border-b-2 px-3 py-2 flex items-baseline gap-2 cursor-pointer transition ${active ? 'border-neon text-neon' : 'border-transparent text-muted hover:text-ink'}`}
  >
    <span className="text-[13px] font-bold whitespace-nowrap">{label}</span>
    <span className={`text-[11px] font-semibold whitespace-nowrap ${active ? 'text-ink' : 'text-muted'}`}>{inr(start)}</span>
  </button>
);

/**
 * One leg of the trip: its itinerary, its cabin tabs and its fare cards. Owns the
 * per-leg supplier state (other-cabin fares + fare rules) so a round trip fetches
 * each leg's cabins independently — the legs are separate trips to TripJack, with
 * their own priceIds.
 *
 * Every leg stays MOUNTED (inactive ones are just hidden) so switching tabs is
 * instant and no leg re-fetches when you come back to it.
 */
function LegFares({ open, leg, pax, searchedCabin, mode, picked, onPick, onBook, hidden, repick = null, pairAnchor = null, anchorLabel = '', nextLabel = '' }) {
  const flight = leg?.flight;
  const [cabin, setCabin] = useState('Economy');
  // priceId → fare-rule summary (object) | false (no rules available).
  // A missing key means "still loading" — the whole cabin shows skeleton cards
  // until every shown fare has an entry here (see `rulesLoading` below).
  const [rules, setRules] = useState({});
  // Fares for the OTHER cabins of this same flight (Business / Premium Economy /
  // First), fetched on open because TripJack only returns the searched cabin.
  const [extraFares, setExtraFares] = useState([]);
  const [cabinsLoading, setCabinsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCabin('Economy');
    setRules({});
    setExtraFares([]);
  }, [open, flight?.sig]);

  // Another leg's pick cleared this one (see onPick in the modal): open on that
  // pick's cabin, which is where the fares that can be booked alongside it are.
  useEffect(() => {
    if (repick?.cabin) setCabin(repick.cabin);
  }, [repick?.cabin]);

  // TripJack returns fares ONLY for the cabin that was searched, so an Economy
  // search hides this flight's Business / Premium-Economy / First fares. Re-run
  // the same search per cabin and merge the fares belonging to THIS flight
  // (matched on its full segment-chain signature). `direction` matters: the
  // return leg of a round trip lives under RETURN, not ONWARD. Cached in the api
  // layer, so a second leg's lookup reuses the same responses.
  useEffect(() => {
    if (!open || !leg?.searchArgs || !flight?.sig) return undefined;
    let alive = true;
    setCabinsLoading(true);
    fetchOtherCabinFares({
      searchArgs: leg.searchArgs,
      signature: flight.sig,
      pax,
      searchedCabin,
      direction: leg.direction || 'ONWARD',
    })
      .then((fares) => { if (alive) setExtraFares(fares || []); })
      .finally(() => { if (alive) setCabinsLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flight?.sig, leg?.direction]);

  // The searched cabin's fares + the other cabins' fares for this same flight,
  // de-duplicated by priceId (a flight can legitimately surface in two searches).
  const mergedFares = (() => {
    const base = Array.isArray(flight?.fares) ? flight.fares : [];
    if (!extraFares.length) return base;
    const seen = new Set(base.map((x) => x.priceId));
    return [...base, ...extraFares.filter((x) => x.priceId && !seen.has(x.priceId))];
  })();
  // SPECIAL RETURN LOCK — the same rule the results list applies, applied again
  // here because this modal is the OTHER way a leg's fare gets changed, and
  // because it is the only place the airline's non-searched cabins (extraFares
  // above) ever surface: those arrive from their own searches and have never been
  // past the results page's filter. Anchored on the FIRST leg that has a pick, so
  // the leg the customer is stepping through follows the one they already
  // settled, and leg 1 itself is never narrowed.
  //
  // `blocked` (this flight sells nothing that pairs) deliberately keeps the WHOLE
  // list: leaving the customer a dialog with no fares in it is a dead end, and
  // CONTINUE still has specialReturnIssue() behind it. See narrowLegToAnchor in
  // FlightResults, which makes the same call for the same reason.
  const pairedFares = pairAnchor ? pairableFares(mergedFares, pairAnchor) : mergedFares;
  const srNarrowed = pairedFares !== mergedFares && pairedFares.length > 0;
  const srBlocked = pairedFares !== mergedFares && pairedFares.length === 0;
  const usableFares = srBlocked ? mergedFares : pairedFares;
  const liveFares = usableFares.length ? usableFares : null;

  // Group the LIVE fares by their real cabin class → one tab per cabin the
  // airline actually sells on this flight, each showing its own "Starting at".
  const liveGroups = liveFares
    ? Object.values(
        liveFares.reduce((acc, x) => {
          const label = cabinName(x.cabin) || cabinName(flight?.cabinClass) || 'Economy';
          (acc[label] = acc[label] || { label, fares: [] }).fares.push(x);
          return acc;
        }, {}),
      ).sort((a, b) => Math.min(...a.fares.map((x) => x.price)) - Math.min(...b.fares.map((x) => x.price)))
    : [];
  const activeGroup = liveGroups.find((g) => g.label === cabin) || liveGroups[0] || null;
  const shownFares = activeGroup ? activeGroup.fares : [];
  const cheapestFare = shownFares.length ? Math.min(...shownFares.map((x) => x.price)) : null;

  // Fetch the REAL cancellation / date-change / seat-chargeable rules, but only
  // for the fares of the ACTIVE cabin tab — merging four cabins can produce a
  // dozen fares, and the customer only ever sees one tab at a time.
  const shownIds = shownFares.map((x) => x.priceId).filter(Boolean).join(',');
  useEffect(() => {
    if (!open || !shownIds) return undefined;
    let alive = true;
    shownIds.split(',').forEach((priceId) => {
      if (rules[priceId] !== undefined) return; // already fetched / in flight
      fetchFareRule(priceId).then((raw) => {
        if (!alive) return;
        const summary = raw ? summarizeFareRule(raw) : null;
        setRules((prev) => ({ ...prev, [priceId]: summary || false }));
      });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shownIds]);

  // Reveal the cards only once EVERY fare of the active cabin has its rules —
  // until then they'd render without their Flexibility / Seats lines and fill in
  // one by one. `giveUp` is the safety valve: fetchFareRule already swallows its
  // own errors, but a request that simply never settles must not leave the
  // customer staring at a skeleton, so after 8s we show whatever we have.
  const [giveUp, setGiveUp] = useState(false);
  useEffect(() => {
    if (!open || !shownIds) return undefined;
    setGiveUp(false);
    const t = setTimeout(() => setGiveUp(true), 8000);
    return () => clearTimeout(t);
  }, [open, shownIds]);
  // `x.priceId &&` matters: a fare with no priceId is never sent to /fare-rule,
  // so waiting on it would hold the skeleton open until the 8s valve fires.
  const rulesLoading = !giveUp && shownFares.some((x) => x.priceId && rules[x.priceId] === undefined);

  if (!flight) {
    return (
      <div className={hidden ? 'hidden' : 'mt-4 rounded-xl border border-dashed border-line bg-night/30 p-8 text-center text-muted text-sm'}>
        Pick a flight for this leg in the results list to see its fares.
      </div>
    );
  }

  // "Recommended" is earned, not decorative: the fare in this cabin with the
  // STRICTLY lowest real cancellation fee. Needs ≥2 fares with known rules, and
  // it must not simply be the cheapest (that already wears "Best price").
  const recommendedId = (() => {
    const scored = shownFares
      .map((x) => ({ f: x, fee: rules[x.priceId] ? rules[x.priceId].cancelFrom : null }))
      .filter((x) => x.fee != null);
    if (scored.length < 2) return null;
    const min = Math.min(...scored.map((x) => x.fee));
    const winners = scored.filter((x) => x.fee === min);
    if (winners.length !== 1) return null;
    if (winners[0].f.price === cheapestFare) return null;
    return winners[0].f.priceId;
  })();

  // Every fare card spans the whole strip's rows so it can share them (see the
  // <HScroller/> below). This and the parent's grid-rows-[…] must always name the
  // same number of rows — pick mode has no BOOK NOW row.
  const cardSpan = mode === 'pick' ? 'row-span-5' : 'row-span-6';

  return (
    <div className={hidden ? 'hidden' : ''}>
      <div className="mt-2">
        {/* Collapsed it reads as a one-liner; the chevron expands the real
            per-segment itinerary (airport + terminal, flight no, cabin, layovers). */}
        <FlightItinerary flight={flight} compact />
      </div>

      {/* This leg's pick was cleared by another leg's (see onPick in the modal) —
          say why, so the missing ✓ reads as a rule rather than a glitch. */}
      {repick && (
        <p className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-xs text-ink">
          {repick.reason === 'family'
            ? <>Please choose this flight&apos;s fare again — a <span className="font-semibold">Special Return</span> fare
                covers the whole trip, so it cannot be mixed with the fare you just chose on your {repick.from || 'other flight'}.</>
            : repick.reason === 'faretype'
            ? <>Please choose this flight&apos;s fare again — every flight in one trip has to be booked on the same
                fare type (Regular, Student or Senior Citizen) as your {repick.from || 'other flight'} fare.</>
            : <>Please choose this flight&apos;s fare again — every flight in one trip has to be booked
                in the same cabin{repick.cabin ? ` (${repick.cabin})` : ''} as your {repick.from || 'other flight'} fare.</>}
        </p>
      )}

      {/* Why this leg is showing what it is showing. Same three answers the
          results list gives under its leg tabs (LegFareNote) — this modal can
          change a leg's fare on its own, so it has to be able to explain the
          same lock rather than let it read as fares that "went missing". */}
      {srBlocked && (
        <p className="mt-3 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-2.5 text-[11px] leading-snug text-ink">
          <span className="font-bold">No fare here pairs with your {anchorLabel || 'first flight'} fare.</span>{' '}
          A <span className="font-semibold">Special Return</span> fare is one airline fare for the whole trip, and this
          flight is not sold with the one you picked. Go back to your {anchorLabel || 'first flight'} and choose a
          regular fare, or pick a different flight for this leg.
        </p>
      )}
      {/* NEITHER half of the narrowing is announced any more. "Special Return
          fares are hidden here" went on 2026-09-24 and "Showing Special Return
          fares only" on 2026-09-25, both on the client's instruction: every fare
          on screen under a narrowing is bookable, so the note described a filter
          rather than a problem. `srNarrowed` still gates the reassurance below —
          under a lock, "pick a different one on the next tab" is not true. Kept
          in step with LegFareNote in pages/FlightResults, which is the same
          removal under the leg tabs. */}
      {!srNarrowed && !srBlocked && !repick && nextLabel && (
        <p className="mt-3 text-[11px] leading-snug text-muted">
          Each flight has its own fare — you can pick a different one for your{' '}
          <span className="text-ink font-semibold">{nextLabel}</span> on the next tab.
        </p>
      )}

      {liveFares ? (
        <>
          {/* One tab per cabin class the airline really sells on this flight, all
              on ONE line (scrolls sideways when a flight sells all four), each
              carrying its own "starting at". The non-searched cabins arrive a
              moment later (one search each). No tagline anywhere on this strip —
              see the CabinTab comment. */}
          <div role="tablist" className="flex items-end gap-1 mt-2 border-b border-line overflow-x-auto nh-scroll">
            {liveGroups.map((g) => (
              <CabinTab
                key={g.label}
                label={g.label}
                start={Math.min(...g.fares.map((x) => x.price))}
                active={activeGroup?.label === g.label}
                onClick={() => setCabin(g.label)}
              />
            ))}
            {cabinsLoading && (
              <span className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-[11px] text-muted whitespace-nowrap">
                <span className="w-3 h-3 rounded-full border-2 border-line border-t-neon animate-spin shrink-0" />
                Checking Premium Economy, Business &amp; First…
              </span>
            )}
          </div>
          {/* The strip is ONE GRID, not a row of independent cards: a column per
              fare (262px, so FOUR fit across the widened dialog and a fifth
              scrolls in) and a row per section, every card spanning all of them
              as a subgrid. That is what puts each card's Baggage / Cancellation /
              Date Change / Seats block — and the divider above it — on the same
              line as its neighbours', so two fares are compared by reading
              across. Row 5 is `1fr`: it takes the slack from the 54vh floor that
              gives the dialog its height, which is why BOOK NOW sits on the
              bottom edge of every card. The floor is relative and so
              self-limiting — on a short screen 54vh is under the natural card
              height and does nothing, and the modal never outgrows its own cap.
              `mode` decides the row COUNT: pick mode has no BOOK NOW, so the
              template and the cards' row-span both drop to five — they must
              always agree, or the cards stop sharing rows. */}
          <HScroller
            className="mt-1.5"
            rowClassName={`grid grid-flow-col auto-cols-[262px] gap-x-2.5 pt-2 pb-2 min-h-[54vh] ${mode === 'pick' ? 'grid-rows-[auto_auto_auto_auto_1fr]' : 'grid-rows-[auto_auto_auto_auto_1fr_auto]'}`}
          >
            {rulesLoading ? shownFares.map((fare, i) => (
              <FareCardSkeleton key={fare.priceId || i} mode={mode} className={cardSpan} />
            )) : shownFares.map((fare) => (
              <LiveFareCard
                key={fare.priceId}
                fare={fare}
                mode={mode}
                picked={mode === 'pick' && picked?.priceId === fare.priceId}
                onPick={onPick}
                onBook={onBook}
                className={cardSpan}
                best={fare.price === cheapestFare}
                recommended={fare.priceId === recommendedId}
                rule={rules[fare.priceId]}
              />
            ))}
          </HScroller>
          {/* Once per leg, only when such a fare is actually on offer here. */}
          {shownFares.some(isExclusiveFare) && <ExclusiveFareNote />}
        </>
      ) : (
        // No live fares for this leg. This used to fall back to the FARE_PLANS mock
        // catalogue — invented prices, invented baggage, invented rules, with a
        // working BOOK NOW. Selling a fare the supplier never quoted is worse than
        // showing nothing, so say what is true and send the customer back to search.
        <div className="mt-4 rounded-xl border border-dashed border-line bg-night/30 p-8 text-center">
          <p className="text-sm font-semibold text-ink">Fare options are not available for this flight</p>
          <p className="text-xs text-muted mt-1.5">
            The airline did not return any fares for this leg. Please search again, or pick another flight.
          </p>
        </div>
      )}
    </div>
  );
}

// "DEPART: DEL - BOM" → "Depart flight"; "FLIGHT 2: BOM - GOI" → "Flight 2".
// The tab labels shout in capitals, which reads as an instruction rather than a
// noun once it is dropped into a sentence.
function legShortLabel(leg) {
  const head = String(leg?.label || '').split(':')[0].trim();
  if (!head) return 'first flight';
  const cased = head.charAt(0).toUpperCase() + head.slice(1).toLowerCase();
  return /^flight/i.test(head) ? cased : `${cased} flight`;
}

// "1 adult", "2 adults · 1 child"
function paxLabel(pax) {
  const parts = [];
  const a = Number(pax?.ADULT) || 0;
  const c = Number(pax?.CHILD) || 0;
  const i = Number(pax?.INFANT) || 0;
  if (a) parts.push(`${a} ADULT${a > 1 ? 'S' : ''}`);
  if (c) parts.push(`${c} CHILD${c > 1 ? 'REN' : ''}`);
  if (i) parts.push(`${i} INFANT${i > 1 ? 'S' : ''}`);
  return parts.join(' · ') || '1 ADULT';
}

export default function FareOptionsModal({ open, onClose, payload }) {
  const [legIdx, setLegIdx] = useState(0);
  // legKey → the fare chosen for that leg (multi-leg mode only).
  const [picks, setPicks] = useState({});
  // Legs whose pick was cleared because another leg's pick came from a different
  // search: { keys: legKey[], cabin, from } — see onPick. null when none.
  const [repick, setRepick] = useState(null);
  const bodyRef = useRef(null);
  // Round-trip Special Return popup: { code, airline } | null — see confirm().
  const [srIssue, setSrIssue] = useState(null);

  // A one-way modal is just the 1-leg case — the caller may still pass the old
  // { flight } shape, so normalise it here rather than forking the whole render.
  const legs = Array.isArray(payload?.legs) && payload.legs.length
    ? payload.legs
    : (payload?.flight
      ? [{ key: 'only', label: '', flight: payload.flight, direction: 'ONWARD', searchArgs: payload.searchArgs }]
      : []);
  const multi = legs.length > 1;

  // Reset each time the modal opens. The dialog renders straight away — the fare
  // cards carry their own skeletons while the rules load, so there is nothing to
  // wait for here; the old 700 ms "Getting Fares…" gate only delayed the very
  // supplier calls the customer is waiting on (<LegFares/> fetches on mount).
  useEffect(() => {
    if (!open) return undefined;
    setLegIdx(0);
    setRepick(null);
    setSrIssue(null);
    // Pre-seed each leg with the fare it already carries (the customer picked it
    // on the results card), so the bar shows a real total on open instead of a
    // dash, and CONTINUE is live if they don't want to change anything.
    setPicks(() => {
      const seed = {};
      for (const l of legs) {
        const chosen = (l.flight?.fares || []).find((x) => x.priceId === l.flight?.priceId);
        if (chosen) seed[l.key] = chosen;
      }
      return seed;
    });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  // Every leg is mounted at once (the inactive ones are merely `hidden`), so a
  // leg change leaves the scroller wherever the PREVIOUS leg was read to —
  // typically part-way down its fare cards, with the new leg's own itinerary
  // line off-screen above. Step to a leg, start at its top.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [legIdx]);

  if (!open || !legs.length) return null;

  // One-way: book straight from the card, exactly as before. Close THIS modal
  // first — the action may open the auth modal (single-slot ModalsHost) or
  // navigate, and closing afterwards would shut the auth modal right back down.
  const onBook = (planOrFare, price) => {
    onClose?.();
    payload?.onBook?.(planOrFare, price);
  };

  // Multi-leg: a card is a choice. Picking the last outstanding leg's fare jumps
  // to whichever leg still has none, so the customer flows through the trip.
  //
  // Every leg's fare has to come from ONE search (see searchTagOf in
  // api/flights.js): each cabin tab beyond the searched one is its own re-run
  // search, and TripJack refuses a trip whose priceIds span two ("Number of
  // PriceId … doesn't match with the requested trips" — the dead-end popup on the
  // seat step). So the NEW pick wins, the same way a fare-family change does on
  // the results page: any other leg picked from a different search is cleared, the
  // jump below walks the customer to it, and it opens on this pick's cabin with a
  // note saying why (`repick`).
  const onPick = (legKey) => (fare) => {
    const tag = fare?.searchTag || '';
    const idx = legs.findIndex((l) => l.key === legKey);
    const next = { ...picks, [legKey]: fare };
    // Two reasons another leg's pick cannot survive this one, reported separately
    // because they ask the customer for different things:
    //   • a different SEARCH — TripJack refuses a trip whose priceIds span two
    //     (the cabin re-run above), see the note on this function;
    //   • a different FARE FAMILY — a Special Return fare is half of ONE
    //     round-trip fare and cannot be mixed (pairableFares).
    // Family is judged only on legs AFTER the one just picked: the earlier legs
    // are the anchor, and changing a later tab must not throw away a choice the
    // customer already settled and moved past.
    const clearedTag = legs.filter((l) => l.key !== legKey && next[l.key] && (next[l.key].searchTag || '') !== tag);
    const clearedFamily = legs.filter((l, i) => i > idx && next[l.key] && !clearedTag.includes(l)
      && !pairableFares([next[l.key]], fare).length);
    const cleared = [...clearedTag, ...clearedFamily];
    for (const l of cleared) delete next[l.key];
    setPicks(next);
    setRepick((prev) => {
      if (cleared.length) {
        return {
          keys: cleared.map((l) => l.key),
          // Cabin wins the wording when both apply: it is the one that also
          // dictates WHICH cabin tab the leg reopens on.
          // Same cabin but another leg on a different fare-type search → say that.
          reason: !clearedTag.length ? 'family'
            : clearedTag.every((l) => cabinName(picks[l.key]?.cabin) === cabinName(fare?.cabin)
              && (picks[l.key]?.pft || '') !== (fare?.pft || '')) ? 'faretype' : 'cabin',
          cabin: cabinName(fare?.cabin),
          from: legs.find((l) => l.key === legKey)?.label || '',
        };
      }
      const keys = (prev?.keys || []).filter((k) => k !== legKey);
      return keys.length ? { ...prev, keys } : null;
    });
    // CHOOSING A FARE CARRIES ON TO THE NEXT FLIGHT. A leg with no fare at all
    // wins over plain order (so a leg just cleared is walked to); otherwise this
    // simply steps forward. It used to move ONLY to a leg with no pick — and every
    // leg opens pre-seeded with the fare its results card already carried, so
    // there was almost never such a leg and picking a fare left the customer
    // sitting on the tab they were already on, with the next flight's fares
    // reachable only by noticing the tab strip.
    const gap = legs.findIndex((l) => !next[l.key]);
    const to = gap > -1 ? gap : Math.min(idx + 1, legs.length - 1);
    if (to !== legIdx) setLegIdx(to);
  };

  // The leg every LATER leg has to pair with — the first one carrying a pick.
  // Never "the other leg": anchoring both ways would lock the trip to whatever was
  // pre-seeded and leave no tab able to change fare family at all.
  const anchorIdx = legs.findIndex((l) => picks[l.key]);
  const anchorPick = anchorIdx > -1 ? picks[legs[anchorIdx].key] : null;

  const chosen = legs.map((l) => picks[l.key]).filter(Boolean);
  const allPicked = chosen.length === legs.length;
  // All-pax total per leg (`total`), falling back to the per-adult price on a
  // mock plan that carries no total.
  const tripTotal = chosen.reduce((sum, f) => sum + (Number(f?.total ?? f?.price) || 0), 0);
  const tripLabel = payload?.mode === 'multicity' ? 'MULTI-CITY' : 'ROUND TRIP';

  const confirm = () => {
    if (!allPicked) return;
    // Round trip: both legs Special Return, same airline, pairing codes matching —
    // or TripJack refuses the review. Stop here and say which.
    if (payload?.mode === 'return') {
      const issue = specialReturnIssue(picks.onward, picks.ret);
      if (issue) {
        setSrIssue({ code: issue, airline: legs[0]?.flight?.airline || '' });
        return;
      }
    }
    onClose?.();
    payload?.onConfirm?.(picks);
  };

  // CONTINUE WALKS THE LEGS — it does not finish the trip from leg 1.
  //
  // Every leg opens pre-seeded with the fare its results card already carried
  // (see the seed above), so on a round trip BOTH legs were ✓ the moment the
  // modal opened and CONTINUE confirmed straight from DEPART. The return leg's
  // own fares — the whole reason this modal has leg tabs — were never seen
  // unless the customer happened to click the RETURN tab.
  //
  // So the button now advances to the next leg and only CONFIRMS on the last
  // one. Going back to an earlier tab and pressing it again simply walks
  // forward from there; the picks themselves are untouched by any of this.
  const lastLeg = legs.length - 1;
  const onLastLeg = legIdx >= lastLeg;
  const missing = legs.filter((l) => !picks[l.key]).length;
  // Advancing needs only the leg ON SCREEN chosen; confirming needs them all.
  const canContinue = onLastLeg ? allPicked : Boolean(picks[legs[legIdx]?.key]);
  const continueLabel = canContinue
    ? 'CONTINUE'
    : (onLastLeg ? `SELECT ${missing} MORE` : 'SELECT A FARE');
  const onContinue = () => {
    if (!canContinue) return;
    if (!onLastLeg) { setLegIdx(legIdx + 1); return; }
    confirm();
  };

  return (
    <div className="fixed inset-0 z-[93] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <SpecialReturnModal
        issue={srIssue}
        ctaLabel="Choose return fare"
        onClose={() => { setSrIssue(null); setLegIdx(legs.length - 1); }}
      />
      {/* Dialog width is FIXED at max-w-6xl. The fare cards are sized to fit four
          across inside it rather than the shell growing to fit them. */}
      <div className="relative w-full max-w-6xl max-h-[92vh] rounded-2xl border border-line bg-panel flex flex-col">
        <div className="px-5 sm:px-6 pt-4 pb-0 shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted"><path fill="currentColor" d="M18.3 7.1 16.9 5.7 12 10.6 7.1 5.7 5.7 7.1l4.9 4.9-4.9 4.9 1.4 1.4 4.9-4.9 4.9 4.9 1.4-1.4-4.9-4.9z" /></svg>
          </button>
          <h2 className="font-display text-base font-extrabold pr-8">Flight Details and Fare Options available for you!</h2>

          {/* Leg tabs — DEPART / RETURN, or Flight 1..N on multi-city. A leg with a
              chosen fare shows a ✓ so the customer can see what's still missing. */}
          {multi && (
            <div className="flex gap-1 mt-3 border-b border-line overflow-x-auto no-scrollbar">
              {legs.map((l, i) => {
                const active = i === legIdx;
                const done = Boolean(picks[l.key]);
                return (
                  <button
                    key={l.key}
                    onClick={() => setLegIdx(i)}
                    className={`shrink-0 px-4 py-2.5 text-[13px] font-bold whitespace-nowrap border-b-2 -mb-px cursor-pointer transition ${active ? 'border-neon text-neon' : 'border-transparent text-muted hover:text-ink'}`}
                  >
                    {done && <span className="text-emerald-400 mr-1.5">✓</span>}
                    {l.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* `bodyRef` — reset to the top on every leg change (see the effect above). */}
        <div ref={bodyRef} className="px-5 sm:px-6 pb-3 overflow-y-auto no-scrollbar flex-1">
          {legs.map((l, i) => (
            <LegFares
              key={l.key}
              open={open}
              leg={l}
              pax={payload?.pax}
              searchedCabin={payload?.searchedCabin}
              mode={multi ? 'pick' : 'book'}
              picked={picks[l.key]}
              onPick={onPick(l.key)}
              onBook={onBook}
              hidden={multi && i !== legIdx}
              repick={repick?.keys.includes(l.key) ? repick : null}
              pairAnchor={multi && i > anchorIdx ? anchorPick : null}
              anchorLabel={anchorIdx > -1 ? legShortLabel(legs[anchorIdx]) : ''}
              nextLabel={multi && i < legs.length - 1 ? legShortLabel(legs[i + 1]) : ''}
            />
          ))}
        </div>

        {/* Combined total + CONTINUE. The trip is only bookable once EVERY leg is
            chosen, so the button confirms on the last leg only — before that it
            carries the customer to the next leg (see onContinue). The caption
            under it names where the button goes, so "Continue" never looks like
            it is about to finish a booking the customer hasn't finished picking. */}
        {multi && (
          <div className="shrink-0 border-t border-line bg-night/60 px-5 sm:px-6 py-2.5 flex items-center justify-end gap-4 rounded-b-2xl">
            <div className="text-right">
              <p className="font-display text-xl font-extrabold text-neon">{allPicked ? inr(tripTotal) : '—'}</p>
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted">{tripLabel} FOR {paxLabel(payload?.pax)}</p>
            </div>
            <div className="text-right">
              <button
                onClick={onContinue}
                disabled={!canContinue}
                className={`px-6 py-2.5 rounded-xl font-bold transition ${canContinue ? 'bg-gradient-to-r from-neon to-mint text-onaccent cursor-pointer hover:brightness-110' : 'border border-line text-muted cursor-not-allowed'}`}
              >
                {continueLabel}
              </button>
              {canContinue && !onLastLeg && (
                <p className="text-[10px] text-muted mt-1.5">Next: {legs[legIdx + 1]?.label || 'next flight'}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
